import { Controller } from 'react-hook-form';
import {
  CapitalActionFormState,
  UpdateCapitalActionFormValues,
} from '../../hooks/useCapitalActionForm';
import ActionSelector from './ActionSelector';

import { Grid } from '@mui/material';
import ErrorBoundary from 'components/ErrorBoundary';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { CapitalActionEntryType } from 'further-types/lp/capital-action';
import { CurrencyCode } from 'further-types/lp/vehicle';
import BasicSelect from 'further-ui/components/BasicSelect';
import FormField from 'further-ui/components/FormField';
import { Column } from 'further-ui/components/Layout';
import api from 'lib/trpcClient';
import useDefaultEntryValues from '../../hooks/useDefaultEntryValues';
import ActionsDetails from './ActionsDetails';
import AdvanceSubscriptionPaymentDetails from './AdvanceSubscriptionPaymentDetails';
import CapitalContributionCallDetails from './CapitalContributionCallDetails';
import CapitalDistributionDetails from './CapitalDistributionDetails';
import CapitalRefundDetails from './CapitalRefundDetails';
import GeneralCapitalCallDetails from './GeneralCapitalCallDetails';
import useAvailableTransactionTypeOptions from './hooks/useAvailableTransactionTypeOptions';

type Props = {
  hasUncalledEqualisations: boolean;
  index: number;
  vehicleId: string;
  baseCurrency: CurrencyCode;
  hasDistributionEntry: boolean;
  entriesCount: number;
};

const CapitalActionEntry: React.FC<Props> = ({
  hasUncalledEqualisations,
  index,
  vehicleId,
  baseCurrency,
  hasDistributionEntry,
  entriesCount,
}) => {
  const { defaultEntryValues } = useDefaultEntryValues();

  const {
    formMethods: { control, watch, setValue, getValues },
  } = useFormWithStateContext<
    UpdateCapitalActionFormValues,
    CapitalActionFormState
  >();

  const entryType = watch(`entries.${index}.entryType`);
  const actions = watch(`entries.${index}.actions`);

  const date = getValues('ledgerDate');

  const [availableActions] =
    api.capitalAction.listAvailableEntryActions.useSuspenseQuery({
      vehicleId,
    });

  const availableTransactionTypes = useAvailableTransactionTypeOptions({
    currentEntryType: entryType,
    currentEntryIndex: index,
    entriesCount,
    hasDistributionEntry,
    hasUncalledEqualisations,
    availableActions,
  });

  return (
    <Column spacing="lg">
      <Grid container>
        <Grid size={{ sm: 12, md: 12, lg: 8 }}>
          <FormField label="Select transaction type" size="md">
            <Controller
              name={`entries.${index}.entryType`}
              control={control}
              render={({
                fieldState: { error },
                field: { onChange, ...rest },
              }) => (
                <BasicSelect
                  fullWidth
                  options={availableTransactionTypes}
                  placeholder="Select transaction type"
                  error={!!error}
                  onChange={(value) => {
                    setValue(`entries.${index}`, defaultEntryValues[value]);
                    onChange(value);
                  }}
                  {...rest}
                />
              )}
            />
          </FormField>
        </Grid>
      </Grid>

      <ErrorBoundary>
        {entryType === CapitalActionEntryType.GeneralCapitalCall ? (
          <GeneralCapitalCallDetails
            index={index}
            vehicleId={vehicleId}
            entryType={entryType}
            baseCurrency={baseCurrency}
          />
        ) : entryType === CapitalActionEntryType.AdvanceSubscriptionPayment ? (
          <AdvanceSubscriptionPaymentDetails
            index={index}
            vehicleId={vehicleId}
            entryType={entryType}
            baseCurrency={baseCurrency}
          />
        ) : entryType === CapitalActionEntryType.CapitalContributionCall ? (
          <CapitalContributionCallDetails
            index={index}
            vehicleId={vehicleId}
            baseCurrency={baseCurrency}
          />
        ) : entryType === CapitalActionEntryType.CapitalRefund ? (
          <CapitalRefundDetails
            index={index}
            vehicleId={vehicleId}
            entryType={entryType}
            baseCurrency={baseCurrency}
          />
        ) : entryType === CapitalActionEntryType.CapitalDistribution ? (
          <CapitalDistributionDetails
            index={index}
            vehicleId={vehicleId}
            entryType={entryType}
            baseCurrency={baseCurrency}
            ledgerDate={date}
          />
        ) : (
          <Grid container>
            <Grid size={{ sm: 12, md: 12, lg: 8 }}>
              <ActionSelector
                index={index}
                vehicleId={vehicleId}
                entryType={entryType}
                baseCurrency={baseCurrency}
                availableActions={
                  availableActions.find((a) => a.entryType === entryType)
                    ?.actions ?? []
                }
              />
            </Grid>
          </Grid>
        )}
      </ErrorBoundary>
      <ErrorBoundary>
        {!!actions.length && (
          <ActionsDetails
            vehicleId={vehicleId}
            entryType={entryType}
            actions={actions}
            entryIndex={index}
          />
        )}
      </ErrorBoundary>
    </Column>
  );
};

export default CapitalActionEntry;
